import { observer } from 'mobx-react';
import * as React from 'react';
import { T } from '../../../../Translations';
import BigCheckmarkCircleIcon from '../../../Cutesies/BigCheckmarkCircleIcon';
import TriangleExclamationIcon from '../../../Cutesies/TriangleExclamationIcon';
import Modal from '../../../Modal';
import { importParticipantsV2Store } from '../Store';
import { isSelectedLicense } from '../Store/Types';

interface SuccessReportProps {
  onClose: () => void;
  onAddAnother: () => void;
}

type ResultVariant = 'accent' | 'primary' | 'destructive' | 'default';

const variantClass: Record<ResultVariant, string> = {
  accent: 'text-accent',
  primary: 'text-primary',
  destructive: 'text-destructive',
  default: 'text-foreground',
};

interface ResultRowProps {
  label: React.ReactNode;
  value: React.ReactNode;
  variant?: ResultVariant;
}

function ResultRow({ label, value, variant = 'default' }: ResultRowProps) {
  return (
    <div className="flex justify-between py-1.5" style={{ fontSize: 'var(--text-sm)' }}>
      <span className="text-muted-foreground">{label}</span>
      <span className={variantClass[variant]} style={{ fontWeight: 'var(--font-weight-medium)' }}>
        {value}
      </span>
    </div>
  );
}

function SuccessReport({ onClose, onAddAnother }: SuccessReportProps) {
  const store = importParticipantsV2Store;
  const state = store.state;

  if (state.kind !== 'submit-succeeded' && state.kind !== 'view-report') {
    return null;
  }

  const result = state.resultResource.payload;
  const { successful, updated, failed, total } = result.summary;
  const domainMismatches = result.domainMismatchAlerts.length;
  const differentOrg = result.participantsOfDifferentOrg.length;
  const hasFailed = failed > 0 || domainMismatches > 0 || differentOrg > 0;
  const allFailed = successful === 0 && updated === 0;

  const groupsCount = store.selectedGroups.filter(
    (g) => g.kind === 'selected' || g.kind === 'new',
  ).length;
  const licensesCount = store.selectedLicenses.filter(isSelectedLicense).length;

  const title = allFailed
    ? 'Import Not Successful'
    : hasFailed
      ? 'Import Partially Successful'
      : 'Participants Saved';

  return (
    <Modal close={onClose} title="">
      <div className="text-center">
        {/* Icon */}
        <div className="flex justify-center mb-4">
          {hasFailed ? (
            <TriangleExclamationIcon width="80" height="80" />
          ) : (
            <BigCheckmarkCircleIcon width="80" height="80" />
          )}
        </div>

        {/* Title */}
        <h2 className="text-xl mb-0.5" style={{ fontWeight: 'var(--font-weight-semibold)' }}>
          <T kind={title} />
        </h2>

        {/* Subtitle */}
        <p className="text-muted-foreground mb-4" style={{ fontSize: 'var(--text-sm)' }}>
          {total} <T kind="total participants" />
        </p>

        {/* Details Box */}
        <div className="bg-muted rounded-lg px-3.5 py-2.5 mb-4">
          {successful > 0 && (
            <ResultRow label={<T kind="Newly Added" />} value={successful} variant="accent" />
          )}
          {updated > 0 && (
            <ResultRow label={<T kind="Updated" />} value={updated} variant="primary" />
          )}
          {failed > 0 && (
            <ResultRow label={<T kind="Failed" />} value={failed} variant="destructive" />
          )}
          {differentOrg > 0 && (
            <ResultRow
              label={<T kind="In Different Organization" />}
              value={differentOrg}
              variant="destructive"
            />
          )}
          {domainMismatches > 0 && (
            <ResultRow
              label={<T kind="Domain Mismatch" />}
              value={domainMismatches}
              variant="destructive"
            />
          )}

          {!allFailed && (
            <>
              <div className="border-t border-border my-1" />
              <ResultRow
                label={<T kind="Groups" />}
                value={<T kind="{{count}} assigned" count={groupsCount} />}
              />
              <ResultRow
                label={<T kind="Licenses" />}
                value={<T kind="{{count}} allocated" count={licensesCount} />}
              />
            </>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-1.5">
          <button
            className="w-full py-2.5 px-4 rounded-lg border-2 border-primary text-primary bg-transparent hover:opacity-90 transition-opacity"
            style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)' }}
            onClick={onAddAnother}
            data-intercom-target="import-participants-v2-add-more"
          >
            <T kind="Add More Participants" />
          </button>
          <button
            className="w-full py-2 text-muted-foreground hover:text-foreground transition-colors"
            style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)' }}
            onClick={onClose}
            data-intercom-target="import-participants-v2-return-to-list"
          >
            <T kind="Return to Participant List" />
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default observer(SuccessReport);
